import {createContext, useContext, useState, PropsWithChildren, useMemo, useCallback, useEffect} from 'react';
import {actions, getShowWeekends, useAppDispatch, useAppSelector} from '@lib/store';
import {dateToLocalDateString} from '@lib/store/src/data/timeslots/dateTimeHelpers';
import {addWeeks, eachDayOfInterval, endOfWeek, getWeek, subWeeks} from 'date-fns';

import {eachWeekOfInterval, getISOWeek, parseISO} from 'date-fns';
import {useTranslation} from 'react-i18next';
import {LocalDateString} from '@lib/store/src/data/timeslots/apiTypes';
import {DateRange} from '@lib/store/src/data/groups/types';
import {useParams} from 'react-router-dom';
import {useBreakPoint} from '@hooks';

const getWeekInfoForRange = (range: DateRange): WeekInfo[] => {
  const start = parseISO(range.startDate);
  const end = parseISO(range.endDate);

  // Get Monday of each week between the start and end dates.
  const weekStarts = eachWeekOfInterval({start, end}, {weekStartsOn: 1});

  return weekStarts.map((weekStart) => {
    const weekEnd = endOfWeek(weekStart, {weekStartsOn: 1});

    return {
      weekNumber: getISOWeek(weekStart),
      days: eachDayOfInterval({
        start: weekStart,
        end: weekEnd,
      }).map((date) => dateToLocalDateString(date)),
    };
  });
};

type WeekInfo = {
  weekNumber: number;
  days: LocalDateString[];
};

type ConnectionsContextType = ReturnType<typeof useContextValue>;

const ConnectionsContext = createContext<ConnectionsContextType | undefined>(undefined);

type Props = {
  dataDateRange: DateRange;
};

const now = new Date();

const currentWeekNumber = getWeek(now);
const nextWeekNumber = getWeek(addWeeks(now, 1));
const previousWeekNumber = getWeek(subWeeks(now, 1));

const useContextValue = ({dataDateRange}: Props) => {
  const {t} = useTranslation();
  const {groupId} = useParams();
  const dispatch = useAppDispatch();

  const breakpoints = useBreakPoint();

  const [selectedDate, setSelectedDate] = useState<LocalDateString>(dateToLocalDateString(now));

  useEffect(() => {
    dispatch(actions.loadShowWeekends());
  }, [dispatch]);

  const _showWeekends = useAppSelector(getShowWeekends);
  const showWeekends = groupId ? _showWeekends : true; // only use the group setting when in group context

  const weekInfoInRange = getWeekInfoForRange(dataDateRange);
  const [selectedWeekNumber, setSelectedWeekNumber] = useState<number>(currentWeekNumber);

  const weekLabel = useMemo(() => {
    const weekLabels: Record<number, string> = {
      [currentWeekNumber]: t('groups:Schedule.ThisWeek'),
      [nextWeekNumber]: t('groups:Schedule.NextWeek'),
      [previousWeekNumber]: t('groups:Schedule.LastWeek'),
    };
    return weekLabels[selectedWeekNumber] ?? t('groups:Schedule.WeekNumber', {number: selectedWeekNumber});
  }, [selectedWeekNumber, t]);

  const moveToNextWeek = useCallback(() => {
    const currentIndex = weekInfoInRange.findIndex((info) => info.weekNumber === selectedWeekNumber);
    const nextIndex = currentIndex + 1;
    if (nextIndex < weekInfoInRange.length) {
      const targetWeekNumber = weekInfoInRange[nextIndex].weekNumber;
      const targetWeekDays = weekInfoInRange[nextIndex].days;

      setSelectedWeekNumber(targetWeekNumber);

      // // If moving to current week, select today, otherwise select first day of the week
      // if (targetWeekNumber === currentWeekNumber) {
      //   setSelectedDate(dateToLocalDateString(now));
      // } else {
      //   setSelectedDate(targetWeekDays[0]);
      // }
    }
  }, [weekInfoInRange, selectedWeekNumber]);

  const moveToPreviousWeek = useCallback(() => {
    const currentIndex = weekInfoInRange.findIndex((info) => info.weekNumber === selectedWeekNumber);
    const prevIndex = currentIndex - 1;
    if (prevIndex >= 0) {
      const targetWeekNumber = weekInfoInRange[prevIndex].weekNumber;
      const targetWeekDays = weekInfoInRange[prevIndex].days;

      setSelectedWeekNumber(targetWeekNumber);

      // // If moving to current week, select today, otherwise select first day of the week
      // if (targetWeekNumber === currentWeekNumber) {
      //   setSelectedDate(dateToLocalDateString(now));
      // } else {
      //   setSelectedDate(targetWeekDays[0]);
      // }
    }
  }, [weekInfoInRange, selectedWeekNumber]);

  const canMoveToNextWeek = selectedWeekNumber !== weekInfoInRange.at(-1)?.weekNumber;
  const canMoveToPreviousWeek = selectedWeekNumber !== weekInfoInRange.at(0)?.weekNumber;

  const daysForSelectedWeek = useMemo(() => {
    const days = weekInfoInRange.find((info) => info.weekNumber === selectedWeekNumber)?.days || [];
    return showWeekends ? days : days.slice(0, 5);
  }, [selectedWeekNumber, showWeekends, weekInfoInRange]);

  const compactView = useMemo(() => {
    return ['small', 'medium'].includes(breakpoints);
  }, [breakpoints]);
  const canShowFullContent = ['small', 'medium', 'large'].includes(breakpoints) || showWeekends;

  return {
    dataDateRange,
    showWeekends,
    weekInfoInRange,
    daysForSelectedWeek,
    weekLabel,
    currentWeekNumber,
    selectedWeekNumber,
    setSelectedWeekNumber,

    moveToNextWeek,
    moveToPreviousWeek,
    canMoveToNextWeek,
    canMoveToPreviousWeek,

    selectedDate,
    setSelectedDate,

    compactView,
    canShowFullContent,
  };
};

export const ConnectionsProvider = ({children, dataDateRange}: PropsWithChildren<Props>) => {
  const value = useContextValue({dataDateRange});

  return <ConnectionsContext.Provider value={value}>{children}</ConnectionsContext.Provider>;
};

export const useConnections = (): ConnectionsContextType => {
  const context = useContext(ConnectionsContext);
  if (context === undefined) {
    throw new Error('useConnections must be used within a ConnectionsProvider');
  }
  return context;
};
